import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { Plus, Pencil } from 'lucide-react';
import RecurringSlotManager from '../../components/timeslot/recurring-slot-manager';
import OneTimeSlotManager from '../../components/timeslot/one-time-slot-manager';
import EditRecurringSlotsManager from '../../components/timeslot/edit-recurring-slots-manager';
import TimeslotManagement from '../../components/timeslot/timeslot-management';
import CollaboratorSelect from '../../components/dashboard/collaborator-select';
import Drawer from '../../components/ui/drawer';
import { useAvailabilityViewModel } from '../../view-model/professional/availability-view-model';
import { useTranslation } from 'react-i18next';
import ShareAvailabilityLink from '../../components/timeslot/ShareAvailabilityLink';
import professionalStore from "../../store/professional-store";
import companyStore from "../../store/company-store";
import {formatTime} from "../../utils/timezone";
import OnboardingTour from '../onboarding/onboarding-tour';
import { SECTION_AVAILABILITY } from '../../constants/onboarding-sections';
import ReviewTutorialButton from '../../components/onboarding/review-tutorial-button';

const AvailabilityPage: React.FC = observer(() => {
  const viewModel = useAvailabilityViewModel();
  const { t } = useTranslation();

  // Charger la compagnie, initialiser le collaborateur connecté par défaut, puis charger les données
  useEffect(() => {
    const companyId = companyStore.getIdCompanySelectedDefault;
    const currentUserId = professionalStore.getId;

    if (!companyId || !currentUserId) {
      return;
    }

    companyStore.setCompanyById(companyId);


    // Sélectionner le collaborateur connecté par défaut
    if (companyStore.selectedCompany?.collaboratorCompanies) {
      const currentCollaborator = companyStore.selectedCompany.collaboratorCompanies.find(
        c => c.collaboratorId === currentUserId
      );
      if (currentCollaborator) {
        // Initialiser le collaborateur - cela déclenchera automatiquement la reaction
        // dans le ViewModel qui appellera handleGetTimeSlotsByProfessional()
        viewModel.setSelectedCollaboratorId(currentCollaborator.collaboratorCompagnieId);
      }
    }
  }, [companyStore.selectedCompany, professionalStore.getId, viewModel]);

  const handleCollaboratorChange = (collaboratorId: string) => {
    viewModel.setSelectedCollaboratorId(collaboratorId);
  };


  return (
    <>
    <OnboardingTour section={SECTION_AVAILABILITY}/>

    <div className="fixed bottom-6 z-50">
      <ReviewTutorialButton section={SECTION_AVAILABILITY} />
    </div>

    <div className="h-full flex flex-col overflow-hidden">
        {/* Collaborator Select */}
        <div className="mb-3 w-full md:w-80">
          <CollaboratorSelect
            value={viewModel.selectedCollaboratorId}
            onChange={handleCollaboratorChange}
          />
        </div>

        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 gap-3">
            <h3 className="text-lg font-medium flex items-center">
                <div className="availability-timezone">
                    {t('availability.timeZone')}: {professionalStore.profileProfessional?.timeZone}{" "}
                    <span className="text-primary dark:text-primary-light font-medium">
                        ({formatTime()})
                    </span>
                </div>
            </h3>
            <div className="flex items-center gap-2">
                <ShareAvailabilityLink/>
                {/* Bouton circulaire pour ajouter des créneaux */}
                <button
                    onClick={() => {
                        viewModel.setSlotType('recurrent');
                        viewModel.toggleDrawer();
                    }}
                    className="w-10 h-10 bg-primary text-white rounded-full hover:bg-primary/90 transition-colors flex items-center justify-center availability-add"
                    aria-label={t('availability.addSlot', 'Ajouter un créneau') || 'Ajouter un créneau'}
                >
                    <Plus className="h-5 w-5"/>
                </button>
                {/* Bouton circulaire pour éditer les créneaux récurrents */}
                <button
                    onClick={() => viewModel.openEditDrawer()}
                    className="w-10 h-10 bg-primary text-white rounded-full hover:bg-primary/90 transition-colors flex items-center justify-center"
                    aria-label={t('availability.editRecurringSlots', 'Éditer les créneaux récurrents') || 'Éditer les créneaux récurrents'}
                >
                    <Pencil className="h-5 w-5"/>
                </button>
            </div>
        </div>

        <div className="flex-1 overflow-hidden">
            <TimeslotManagement/>
        </div>

        {/* Drawer pour ajouter des créneaux */}
        <Drawer
            onClose={() => viewModel.closeDrawer()}
        width="700px"
        className="pb-16 sm:pb-0"
      >
        {viewModel.getSlotType === null ? (
          <div className="flex flex-col items-center justify-center h-64 text-center p-4">
            <div className="text-gray-400 mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('availability.selectSlotType')}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('availability.selectSlotTypeDescription')}
            </p>
          </div>
        ) : viewModel.getSlotType === 'recurrent' ? (
          <RecurringSlotManager />
        ) : (
          <OneTimeSlotManager />
        )}
      </Drawer>

      {/* Drawer pour éditer les créneaux récurrents */}
      <Drawer
        onClose={() => viewModel.closeEditDrawer()}
        width="700px"
        className="pb-16 sm:pb-0"
        hideSlotTypeSelector={true}
        isOpen={viewModel.getEditDrawerOpen}
        isVisible={viewModel.getIsEditVisibleDrawer}
      >
        {viewModel.isEditMode && <EditRecurringSlotsManager />}
      </Drawer>
    </div>
    </>
  );
});

export default AvailabilityPage;
